import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Skeleton}.
 *
 * All sizing and color classes resolve to design tokens from @today/tokens
 * (resolved per bound brand). No hardcoded values.
 *
 * @remarks
 * - Supports three shape variants: text (h-4, rounded), rect (default), circle
 * - Pulse animation for loading effect
 *
 * @example
 * ```tsx
 * const classes = skeletonVariants({ shape: 'text' })
 * // Returns: "animate-pulse bg-ds-surface-muted rounded h-4"
 * ```
 */
export const skeletonVariants = cva('animate-pulse bg-ds-surface-muted', {
	variants: {
		shape: {
			text: 'rounded h-4',
			rect: 'rounded-ds-md',
			circle: 'rounded-full',
		},
	},
	defaultVariants: { shape: 'rect' },
})

/**
 * Props for the {@link Skeleton} component.
 *
 * @property shape - Skeleton shape: 'text' (h-4, rounded) | 'rect' (default) | 'circle'
 * @property className - Optional additional Tailwind classes (use for width/height)
 *
 * @example
 * ```tsx
 * <Skeleton shape="text" className="w-full" />
 * ```
 */
export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof skeletonVariants> {}

/**
 * Skeleton component for loading placeholders.
 *
 * @remarks
 * - **Styling:** All colors use design tokens from `@today/tokens` (resolved per bound brand).
 *   No hardcoded values.
 * - **Shapes:** Text (h-4, text lines), rect (rectangles, default), circle (avatars).
 * - **Animation:** CSS pulse animation.
 * - **Sizing:** Use className with Tailwind width/height utilities (w-*, h-*).
 * - **Usage:** Content loading states, lazy-loaded components, skeleton screens.
 *
 * @example Basic usage
 * ```tsx
 * import { Skeleton } from '@today/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       {isLoading ? (
 *         <>
 *           <Skeleton shape="circle" className="h-12 w-12" />
 *           <Skeleton shape="text" className="w-full" />
 *           <Skeleton shape="text" className="w-3/4" />
 *         </>
 *       ) : (
 *         <UserProfile />
 *       )}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example Card skeleton
 * ```tsx
 * <div className="space-y-2">
 *   <Skeleton shape="rect" className="h-32 w-full" />
 *   <Skeleton shape="text" className="w-full" />
 *   <Skeleton shape="text" className="w-2/3" />
 * </div>
 * ```
 *
 * @see {@link skeletonVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/skeleton/skeleton.stories.tsx for visual examples
 */
export function Skeleton({ shape, className, ...props }: SkeletonProps) {
	return <div {...props} className={cn(skeletonVariants({ shape }), className)} />
}
